import { MessageDescriptor } from "@lingui/core";
import { msg, t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { SelectableTile } from "@whoosh/design-system/src/components/controls/SelectableTile";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";
import { HStack } from "@whoosh/design-system/src/components/primitive";

import {
  HoleCount,
  TeeSheetHoleCountSelectorControl_facility$key,
} from "./__generated__/TeeSheetHoleCountSelectorControl_facility.graphql";

type HoleOption = {
  value: HoleCount | null;
  label: MessageDescriptor;
};

const options: HoleOption[] = [
  {
    value: null,
    label: msg`All`,
  },
  {
    value: "EIGHTEEN",
    label: msg`Eighteen`,
  },
  {
    value: "NINE",
    label: msg`Nine`,
  },
];

type Props = {
  onChange: (holeCount: HoleCount | null) => void;
  value: HoleCount | null;
  facilityRef?: TeeSheetHoleCountSelectorControl_facility$key | null;
};

export const TeeSheetHoleCountSelectorControl = ({
  onChange,
  value,
  facilityRef,
}: Props) => {
  const { i18n } = useLingui();
  const data = useFragment(
    TeeSheetHoleCountSelectorControlFragmentString,
    facilityRef
  );

  const courseLayouts =
    data?.courseLayouts?.edges?.map((edge) => edge?.node) ?? [];

  const hasNine = courseLayouts.some(
    (layout) => layout?.holeCount === "NINE" && layout?.enabled
  );
  const hasEighteen = courseLayouts.some(
    (layout) => layout?.holeCount === "EIGHTEEN" && layout?.enabled
  );
  /**
   * If we did not get passed a facility ref -- we show our filters just to be safe. (and because they are used on cross facility screens).
   *
   * If we DID get passed a facility ref -- we only show our filters if we have both eighteen and nine course layouts.
   */
  const hasBothEighteenAndNine = !data || (hasNine && hasEighteen);

  return hasBothEighteenAndNine ? (
    <VStack gap={4}>
      <Text variant="mainSmallSemiBold">{t`Hole Count`}</Text>
      <HStack gap={2}>
        {options.map((option) => (
          <SelectableTile
            isSmall
            align="center"
            key={option.value ?? "all"}
            title={i18n._(option.label)}
            setSelected={(isSelected) => {
              if (isSelected) {
                onChange(option.value);
              }
            }}
            isSelected={value === option.value}
          />
        ))}
      </HStack>
    </VStack>
  ) : null;
};

export const TeeSheetHoleCountSelectorControlFragmentString = graphql`
  fragment TeeSheetHoleCountSelectorControl_facility on Facility {
    id
    courseLayouts {
      edges {
        node {
          enabled
          holeCount
        }
      }
    }
  }
`;
